import React, { useEffect, useState, useMemo, useRef } from 'react';
import { tokens, type } from '../theme';
import { useSearchParams } from 'react-router-dom';
import FilterBar from '../components/FilterBar';
import { SEASONS, QOP_AGE_GROUPS, DEFAULT_SEASON, DEFAULT_AGE_GROUP, DEFAULT_DIVISION_NAME } from '../config/seasons';
import { getQoPHistory, getConferencesByDivision, getDivisions, fetchSeasons } from '../services/api';
import * as d3 from 'd3';

import {
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Avatar,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

// Distinct colors for team lines
const TEAM_COLORS = [
  // Categorical series palette — deliberately multi-hue so 16 team lines stay
  // distinguishable. Not part of the brand palette in theme.js.
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#00838f', '#c2185b', '#455a64', '#e64a19', '#1565c0',
  '#2e7d32', '#ef6c00', '#6a1b9a', '#00695c', '#ad1457',
  '#37474f', '#bf360c', '#0277bd', '#558b2f', '#e65100',
  '#4a148c', '#004d40', '#880e4f', '#263238'
];

// De-overlap: push labels apart vertically so none overlap
function deOverlap(labels, minGap) {
  if (labels.length <= 1) return labels;
  const sorted = [...labels].sort((a, b) => a.y - b.y);
  for (let pass = 0; pass < 20; pass++) {
    let moved = false;
    for (let i = 1; i < sorted.length; i++) {
      const gap = sorted[i].y - sorted[i - 1].y;
      if (gap < minGap) {
        const shift = (minGap - gap) / 2 + 0.5;
        sorted[i - 1].y -= shift;
        sorted[i].y += shift;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return sorted;
}

// D3 Chart component
const QoPChart = ({ qopData, chartData, viewMode, qualification, scoreRange, isMobile }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Observe container width for responsiveness
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const svgContent = useMemo(() => {
    if (!qopData || !chartData || chartData.length === 0 || containerWidth === 0) return null;

    const labelMargin = isMobile ? 82 : 120;
    // Right-hand labels duplicate the left on a phone; dropping them gives the plot
    // back ~90px instead of clipping both columns.
    const showRightLabels = !isMobile;
    // Clear of the y-axis tick numbers, which end at x=-8.
    const LABEL_GUTTER = 26;
    const margin = {
      top: 15,
      right: showRightLabels ? labelMargin + 10 : 14,
      bottom: 35,
      left: labelMargin + 40,
    };
    const width = containerWidth;
    const height = isMobile ? 350 : 500;
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    if (innerW <= 0 || innerH <= 0) return null;

    const isRank = viewMode === 'rank';
    const suffix = isRank ? '_rank' : '_score';

    // X scale: one point per week
    const xScale = d3.scalePoint()
      .domain(chartData.map(d => d.week))
      .range([0, innerW]);

    // Y scale
    let yDomainMin, yDomainMax;
    if (isRank) {
      const maxRank = Math.max(
        ...qopData.teams.flatMap(t => t.data.map(d => d.rank)),
        qualification.premier + 1
      );
      yDomainMin = 1;
      yDomainMax = maxRank;
    } else {
      yDomainMin = scoreRange.min;
      yDomainMax = scoreRange.max;
    }

    const yScale = d3.scaleLinear()
      .domain(isRank ? [yDomainMax, yDomainMin] : [yDomainMin, yDomainMax])
      .range([innerH, 0]);

    // Grid lines
    const yTicks = yScale.ticks(isRank ? Math.min(yDomainMax, 10) : 6);
    const xTicks = chartData.map(d => d.week);

    // Build team line data
    const teamLines = qopData.teams.map((team, index) => {
      const color = TEAM_COLORS[index % TEAM_COLORS.length];
      const points = chartData
        .map(d => {
          const val = d[`${team.team_name}${suffix}`];
          if (val == null) return null;
          return { x: xScale(d.week), y: yScale(val), value: val };
        })
        .filter(Boolean);
      return { team, color, points, index };
    });

    // Build labels for left and right sides
    const leftLabels = [];
    const rightLabels = [];

    teamLines.forEach(({ team, color, points }) => {
      if (points.length > 0) {
        leftLabels.push({ name: team.team_name, y: points[0].y, color });
      }
      if (points.length > 0) {
        rightLabels.push({ name: team.team_name, y: points[points.length - 1].y, color });
      }
    });

    const labelGap = isMobile ? 10 : 11;
    const adjustedLeft = deOverlap(leftLabels, labelGap);
    const adjustedRight = deOverlap(rightLabels, labelGap);

    // Clamp labels to chart area
    adjustedLeft.forEach(l => { l.y = Math.max(4, Math.min(innerH - 4, l.y)); });
    adjustedRight.forEach(l => { l.y = Math.max(4, Math.min(innerH - 4, l.y)); });

    // Line generator
    const lineGen = d3.line()
      .x(d => d.x)
      .y(d => d.y)
      .curve(d3.curveMonotoneX);

    // Qualification zones (rank view only)
    const qualZones = [];
    if (isRank) {
      // Championship zone
      const champY1 = yScale(1);
      const champY2 = yScale(qualification.championship);
      qualZones.push(
        <rect key="champ-zone" x={0} y={Math.min(champY1, champY2)}
          width={innerW} height={Math.abs(champY2 - champY1)}
          fill="#ffd700" opacity={0.08} />
      );
      // Premier zone
      const premY1 = yScale(qualification.championship + 1);
      const premY2 = yScale(qualification.premier);
      qualZones.push(
        <rect key="prem-zone" x={0} y={Math.min(premY1, premY2)}
          width={innerW} height={Math.abs(premY2 - premY1)}
          fill={tokens.navy} opacity={0.06} />
      );
      // Championship line
      const champLineY = yScale(qualification.championship);
      qualZones.push(
        <line key="champ-line" x1={0} y1={champLineY} x2={innerW} y2={champLineY}
          stroke="#daa520" strokeWidth={2} />
      );
      // Premier line
      const premLineY = yScale(qualification.premier);
      qualZones.push(
        <line key="prem-line" x1={0} y1={premLineY} x2={innerW} y2={premLineY}
          stroke="#6c757d" strokeWidth={1.5} strokeDasharray="5 5" />
      );
    }

    const fontSize = isMobile ? 8 : 9;

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Grid */}
          {yTicks.map(tick => (
            <line key={`grid-y-${tick}`} x1={0} y1={yScale(tick)} x2={innerW} y2={yScale(tick)}
              stroke="#e9ecef" strokeDasharray="3 3" />
          ))}
          {xTicks.map(tick => (
            <line key={`grid-x-${tick}`} x1={xScale(tick)} y1={0} x2={xScale(tick)} y2={innerH}
              stroke="#e9ecef" strokeDasharray="3 3" />
          ))}

          {/* Qualification zones */}
          {qualZones}

          {/* Team lines */}
          {teamLines.map(({ team, color, points }) => (
            <g key={team.team_id}>
              <path
                d={lineGen(points)}
                fill="none"
                stroke={color}
                strokeWidth={2}
              />
              {points.map((pt, i) => (
                <circle key={i} cx={pt.x} cy={pt.y} r={3}
                  fill={color} stroke="white" strokeWidth={1} />
              ))}
            </g>
          ))}

          {/* Y axis */}
          {yTicks.map(tick => (
            <text key={`y-${tick}`} x={-8} y={yScale(tick)}
              textAnchor="end" dominantBaseline="middle"
              fontSize={isMobile ? 10 : 11} fill="#6c757d">
              {isRank ? tick : tick.toFixed(0)}
            </text>
          ))}

          {/* X axis */}
          {xTicks.map(tick => (
            <text key={`x-${tick}`} x={xScale(tick)} y={innerH + 20}
              textAnchor="middle" fontSize={isMobile ? 10 : 11} fill="#6c757d">
              {tick}
            </text>
          ))}

          {/* Left labels — truncated to the reserved gutter so they never clip */}
          {adjustedLeft.map((label, i) => (
            <text key={`left-${i}`} x={-LABEL_GUTTER} y={label.y}
              textAnchor="end" dominantBaseline="middle"
              fontSize={fontSize} fontWeight={600} fill={label.color}>
              <title>{label.name}</title>
              {truncateLabel(label.name, isMobile ? 11 : 20)}
            </text>
          ))}

          {/* Right labels — desktop only */}
          {showRightLabels && adjustedRight.map((label, i) => (
            <text key={`right-${i}`} x={innerW + 8} y={label.y}
              textAnchor="start" dominantBaseline="middle"
              fontSize={fontSize} fontWeight={600} fill={label.color}>
              {label.name}
            </text>
          ))}
        </g>
      </svg>
    );
  }, [qopData, chartData, viewMode, qualification, scoreRange, isMobile, containerWidth]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {svgContent}
    </div>
  );
};


// Trim a team name to fit the chart's label gutter, keeping it recognisable.
const truncateLabel = (name, maxChars) =>
  name.length > maxChars ? `${name.slice(0, maxChars - 1).trimEnd()}…` : name;

const Analytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter state — no division filter, hardcoded to Homegrown.
  // Filters are mirrored into the URL so a QoP view can be shared by link.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSeason, setSelectedSeason] = useState(searchParams.get('season') || DEFAULT_SEASON);
  // Season list comes from the API so a new season needs no frontend deploy;
  // SEASONS is the build-time fallback.
  const [seasons, setSeasons] = useState(SEASONS);
  useEffect(() => {
    let active = true;
    fetchSeasons()
      .then(data => { if (active && data?.seasons?.length) setSeasons(data.seasons); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const [selectedAgeGroup, setSelectedAgeGroup] = useState(searchParams.get('age_group') || DEFAULT_AGE_GROUP);
  const [selectedConference, setSelectedConference] = useState(
    searchParams.get('conference') || 'mid-atlantic-pending'
  );

  // Keep the URL in step with the active filters. The conference sentinel is
  // resolved to a real id once conferences load, so only persist real values.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('season', selectedSeason);
    next.set('age_group', selectedAgeGroup);
    if (selectedConference && selectedConference !== 'mid-atlantic-pending') {
      next.set('conference', selectedConference);
    }
    setSearchParams(next, { replace: true });
  }, [selectedSeason, selectedAgeGroup, selectedConference, setSearchParams]);
  // QoP exists only for League play in U13/U14, so this is fixed rather than a filter.
  const selectedCompetition = 'League';

  // Data state
  const [divisions, setDivisions] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [qopData, setQopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View toggle
  const [viewMode, setViewMode] = useState('rank'); // 'rank' or 'score'

  // Find the Homegrown Division ID
  const homegroundDivisionId = useMemo(() => {
    if (!Array.isArray(divisions) || divisions.length === 0) return null;
    const hg = divisions.find(d => d.division_name === DEFAULT_DIVISION_NAME);
    return hg ? hg.id : null;
  }, [divisions]);

  // Load divisions when season changes
  useEffect(() => {
    const loadDivisions = async () => {
      try {
        const divisionsData = await getDivisions(selectedSeason).catch(err => {
          console.error('Error loading divisions:', err);
          return [];
        });
        setDivisions(Array.isArray(divisionsData) ? divisionsData : []);
      } catch (error) {
        console.error('Error loading divisions:', error);
        setDivisions([]);
      }
    };

    loadDivisions();
  }, [selectedSeason]);

  // Load conferences when division or season changes
  useEffect(() => {
    const loadConferences = async () => {
      if (!homegroundDivisionId) {
        setConferences([]);
        return;
      }

      try {
        const conferencesData = await getConferencesByDivision(homegroundDivisionId, selectedSeason);
        const confArray = Array.isArray(conferencesData) ? conferencesData : [];
        setConferences(confArray);

        // If we're still waiting for Mid-Atlantic default, set it now
        if (selectedConference === 'mid-atlantic-pending' && confArray.length > 0) {
          const midAtlantic = confArray.find(c => c.conference_name === 'Mid-Atlantic');
          if (midAtlantic) {
            setSelectedConference(midAtlantic.id.toString());
          } else {
            setSelectedConference(confArray[0].id.toString());
          }
        }
      } catch (error) {
        console.error('Error loading conferences:', error);
        setConferences([]);
      }
    };

    loadConferences();
  }, [homegroundDivisionId, selectedSeason, selectedConference]);

  // Load QoP data when filters change
  useEffect(() => {
    const loadQoPData = async () => {
      if (!homegroundDivisionId) {
        // No Homegrown division for this season yet — clear rather than leaving the
        // previous season's data showing under the new season's heading.
        setQopData(null);
        setLoading(false);
        return;
      }
      if (selectedConference === 'mid-atlantic-pending') return;

      setLoading(true);
      setError(null);
      try {
        const conferenceId = selectedConference !== 'all' ? selectedConference : undefined;

        const data = await getQoPHistory(
          selectedAgeGroup,
          conferenceId,
          homegroundDivisionId,
          selectedSeason,
          selectedCompetition
        );
        setQopData(data);
      } catch (error) {
        console.error('Error loading QoP data:', error);
        setError(error.message);
        setQopData(null);
      } finally {
        setLoading(false);
      }
    };

    loadQoPData();
  }, [selectedAgeGroup, selectedConference, homegroundDivisionId, selectedSeason, selectedCompetition]);

  // Handle filter changes
  const handleSeasonChange = (e) => {
    setSelectedSeason(e.target.value);
    setSelectedConference('mid-atlantic-pending');
  };
  const handleAgeGroupChange = (e) => setSelectedAgeGroup(e.target.value);
  const handleConferenceChange = (e) => setSelectedConference(e.target.value);

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const showChart = selectedConference !== 'all' && selectedConference !== 'mid-atlantic-pending';

  // MLS NEXT locks Quality of Play when the season ends, but the scraper keeps
  // recording the same figures weekly — 10 identical weeks after 2026-05-03 in the
  // 25/26 data. Left in, they flatten the right-hand end of the chart and make every
  // team's trend read as "no change". Trim the frozen tail back to the last week the
  // numbers actually moved.
  const liveQopData = useMemo(() => {
    if (!qopData || !qopData.teams || !qopData.weeks || qopData.weeks.length < 2) return qopData;

    const signature = (week) => qopData.teams
      .map((t) => {
        const d = t.data.find(x => x.week === week);
        return d ? `${t.team_id}:${d.qop_score}:${d.rank}` : '';
      })
      .join('|');

    const weeks = qopData.weeks;
    let last = weeks.length - 1;
    const sigs = {};
    const sigOf = (i) => (sigs[i] !== undefined ? sigs[i] : (sigs[i] = signature(weeks[i])));
    while (last > 0 && sigOf(last) === sigOf(last - 1)) last -= 1;

    if (last === weeks.length - 1) return qopData;

    const keep = new Set(weeks.slice(0, last + 1));
    return {
      ...qopData,
      weeks: weeks.slice(0, last + 1),
      teams: qopData.teams.map(t => ({ ...t, data: t.data.filter(d => keep.has(d.week)) })),
    };
  }, [qopData]);

  // Compressed chart data: first 3 + last 3 weeks (with overlap handling)
  const chartData = useMemo(() => {
    if (!liveQopData || !liveQopData.teams || !liveQopData.weeks) return [];

    const allWeeks = liveQopData.weeks;
    let selectedWeeks;

    if (allWeeks.length <= 6) {
      selectedWeeks = allWeeks;
    } else {
      const first3 = allWeeks.slice(0, 3);
      const last3 = allWeeks.slice(-3);
      const weekSet = new Set([...first3, ...last3]);
      selectedWeeks = allWeeks.filter(w => weekSet.has(w));
    }

    return selectedWeeks.map((week) => {
      const weekDate = new Date(week + 'T00:00:00');
      const label = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const point = { week: label, weekRaw: week };

      liveQopData.teams.forEach((team) => {
        const weekData = team.data.find(d => d.week === week);
        if (weekData) {
          point[`${team.team_name}_rank`] = weekData.rank;
          point[`${team.team_name}_score`] = weekData.qop_score;
        }
      });

      return point;
    });
  }, [liveQopData]);

  // Compute score range for dynamic y-axis
  const scoreRange = useMemo(() => {
    if (!liveQopData || !liveQopData.teams) return { min: 60, max: 90 };

    const allScores = [];
    liveQopData.teams.forEach(team => {
      team.data.forEach(d => {
        if (d.qop_score != null) allScores.push(d.qop_score);
      });
    });

    if (allScores.length === 0) return { min: 60, max: 90 };

    const min = Math.min(...allScores);
    const max = Math.max(...allScores);
    const paddedMin = Math.floor((min - 2) / 2) * 2;
    const paddedMax = Math.ceil((max + 2) / 2) * 2;
    return { min: Math.max(0, paddedMin), max: Math.min(100, paddedMax) };
  }, [liveQopData]);

  // Get latest data for the table
  const latestTeamData = useMemo(() => {
    if (!liveQopData || !liveQopData.teams) return [];

    return liveQopData.teams.map((team, index) => {
      const latestWeek = team.data.length > 0 ? team.data[team.data.length - 1] : null;
      const prevWeek = team.data.length > 1 ? team.data[team.data.length - 2] : null;

      let rankChange = 0;
      if (latestWeek && prevWeek) {
        rankChange = prevWeek.rank - latestWeek.rank;
      }

      return {
        team_name: team.team_name,
        team_id: team.team_id,
        logo_url: team.logo_url,
        rank: latestWeek ? latestWeek.rank : 999,
        qop_score: latestWeek ? latestWeek.qop_score : null,
        att_score: latestWeek ? latestWeek.att_score : null,
        def_score: latestWeek ? latestWeek.def_score : null,
        rankChange,
        color: TEAM_COLORS[index % TEAM_COLORS.length],
      };
    }).sort((a, b) => a.rank - b.rank);
  }, [liveQopData]);

  const qualification = qopData?.qualification || { championship: 4, premier: 8 };

  // Show error
  if (error) {
    return (
      <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh', p: 4 }}>
        <Typography variant="h6" color="error">
          Error loading analytics: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

      {/* Filter Bar */}
        <FilterBar
          filters={[
            {
              key: 'season', label: 'Season', value: selectedSeason,
              options: seasons.map(x => ({ value: x, label: x })),
              onChange: v => handleSeasonChange({ target: { value: v } }),
              isDefault: v => v === DEFAULT_SEASON,
            },
            {
              key: 'age', label: 'Age group', value: selectedAgeGroup,
              options: QOP_AGE_GROUPS.map(x => ({ value: x, label: x })),
              onChange: v => handleAgeGroupChange({ target: { value: v } }),
              isDefault: v => v === DEFAULT_AGE_GROUP,
            },
            {
              key: 'conference', label: 'Conference', value: selectedConference,
              options: (Array.isArray(conferences) ? conferences : [])
                .map(c => ({ value: String(c.id), label: c.conference_name })),
              onChange: v => handleConferenceChange({ target: { value: v } }),
            },
          ]}
        />

      {/* View Toggle — only show when chart is visible */}
      {showChart && (
        <Box sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&.Mui-selected': {
                  backgroundColor: tokens.navy,
                  color: 'white',
                  '&:hover': { backgroundColor: '#1a3a5c' }
                }
              }
            }}
          >
            <ToggleButton value="rank">QoP Rank</ToggleButton>
            <ToggleButton value="score">QoP Score</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Main Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : !qopData || !Array.isArray(qopData.teams) || qopData.teams.length === 0 ? (
        <Box sx={{ textAlign: 'center', my: 5, px: 3 }}>
          {!homegroundDivisionId ? (
            <>
              <Typography variant="h6">
                {`No Quality of Play data for ${selectedSeason} yet`}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                MLS NEXT publishes Quality of Play once the season is under way.
                It will appear here as soon as it does.
              </Typography>
              {SEASONS.indexOf(selectedSeason) + 1 < SEASONS.length && (
                <Button
                  variant="outlined"
                  onClick={() => setSelectedSeason(SEASONS[SEASONS.indexOf(selectedSeason) + 1])}
                  sx={{ textTransform: 'none', color: tokens.navy, borderColor: tokens.navy }}
                >
                  {`View ${SEASONS[SEASONS.indexOf(selectedSeason) + 1]} instead`}
                </Button>
              )}
            </>
          ) : (
            <Typography variant="h6" color="textSecondary">
              No Quality of Play data for the selected filters.
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {/* D3 Chart — only show for specific conference */}
          {showChart && (
            <Box sx={{ px: isMobile ? 1.5 : 3, pb: 2 }}>
              <Paper sx={{ p: isMobile ? 1 : 2, overflow: 'hidden' }}>
                <QoPChart
                  qopData={qopData}
                  chartData={chartData}
                  viewMode={viewMode}
                  qualification={qualification}
                  scoreRange={scoreRange}
                  isMobile={isMobile}
                />

                {/* Qualification legend below chart (rank view only) */}
                {viewMode === 'rank' && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 3,
                    px: 2,
                    py: 1,
                    borderTop: '1px solid #e9ecef',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 20, height: 2, backgroundColor: '#daa520' }} />
                      <Typography variant="caption" sx={{ color: '#daa520', fontWeight: 600, fontSize: type.label }}>
                        Championship (Seeds 1-{qualification.championship})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 20, height: 2, backgroundColor: tokens.inkMuted, borderTop: '1px dashed #6c757d' }} />
                      <Typography variant="caption" sx={{ color: tokens.inkMuted, fontWeight: 600, fontSize: type.label }}>
                        Premier (Seeds {qualification.championship + 1}-{qualification.premier})
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* Data Table */}
          <Box sx={{ px: isMobile ? 1.5 : 3, pb: 3 }}>
            <Paper sx={{ overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  sx={{
                    minWidth: isMobile ? 500 : '100%',
                    '& th, & td': {
                      padding: isMobile ? '10px 8px' : '14px 12px',
                      borderBottom: '1px solid #f1f3f4',
                    },
                    '& th': {
                      backgroundColor: '#fafafa',
                      fontWeight: 600,
                      color: tokens.inkMuted,
                      fontSize: isMobile ? type.label : type.caption,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      textAlign: 'center'
                    },
                    '& td': {
                      fontSize: isMobile ? type.caption : type.body,
                      color: tokens.ink
                    }
                  }}
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 50, textAlign: 'center' }}>#</TableCell>
                      <TableCell sx={{ width: 40 }}></TableCell>
                      <TableCell sx={{ textAlign: 'left' }}>Team</TableCell>
                      <TableCell sx={{ width: 70, textAlign: 'center' }}>QoP</TableCell>
                      <TableCell sx={{ width: 70, textAlign: 'center' }}>ATT</TableCell>
                      <TableCell sx={{ width: 70, textAlign: 'center' }}>DEF</TableCell>
                      <TableCell sx={{ width: 70, textAlign: 'center' }}>Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {latestTeamData.map((team) => {
                      let rowBg = 'transparent';
                      if (team.rank <= qualification.championship) {
                        rowBg = 'rgba(255, 215, 0, 0.08)';
                      } else if (team.rank <= qualification.premier) {
                        rowBg = 'rgba(25, 118, 210, 0.06)';
                      }

                      return (
                        <TableRow
                          key={team.team_id}
                          sx={{
                            backgroundColor: rowBg,
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                        >
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>
                            {team.rank}
                          </TableCell>
                          <TableCell>
                            {team.logo_url ? (
                              <Avatar
                                src={team.logo_url}
                                alt={team.team_name}
                                sx={{ width: 24, height: 24 }}
                                variant="square"
                              />
                            ) : (
                              <Box sx={{ width: 24, height: 24 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {showChart && (
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 24,
                                    borderRadius: 1,
                                    backgroundColor: team.color,
                                    flexShrink: 0
                                  }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isMobile ? 120 : 200
                                }}
                              >
                                {team.team_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>
                            {team.qop_score != null ? team.qop_score.toFixed(1) : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {team.att_score != null ? team.att_score.toFixed(1) : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {team.def_score != null ? team.def_score.toFixed(1) : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {team.rankChange > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.win }}>
                                <TrendingUpIcon sx={{ fontSize: 18 }} />
                                <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.25 }}>
                                  {team.rankChange}
                                </Typography>
                              </Box>
                            ) : team.rankChange < 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.loss }}>
                                <TrendingDownIcon sx={{ fontSize: 18 }} />
                                <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.25 }}>
                                  {Math.abs(team.rankChange)}
                                </Typography>
                              </Box>
                            ) : (
                              <TrendingFlatIcon sx={{ fontSize: 18, color: tokens.inkFaint }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {/* Qualification Legend */}
              <Box sx={{
                display: 'flex',
                gap: 3,
                px: 2,
                py: 1.5,
                borderTop: '1px solid #e9ecef',
                backgroundColor: '#fafafa'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 12, backgroundColor: 'rgba(255, 215, 0, 0.2)', border: '1px solid #daa520', borderRadius: 0.5 }} />
                  <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                    Championship (Seeds 1-{qualification.championship})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 12, backgroundColor: tokens.navyTint, border: `1px solid ${tokens.navy}`, borderRadius: 0.5 }} />
                  <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                    Premier (Seeds {qualification.championship + 1}-{qualification.premier})
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Analytics;
